import { type App } from "vue";
import { logger } from "@/services/logger/logger.service";
import { type Router } from "vue-router";
import * as Sentry from "@sentry/vue";
import { configService } from "@/services/config.service";

interface SentryConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  enabled?: boolean;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
}

export function setupSentry(app: App, router: Router) {
  // Hardcoded Sentry configuration
  const hardcodedConfig: SentryConfig = {
    dsn: "https://b1cebb0076ae7dc8bee8fb48ced9afcc@o4509556868710400.ingest.us.sentry.io/4509578338828288",
    environment: window.location.hostname || "localhost",
    release: import.meta.env.VITE_APP_VERSION || "1.0.0",
    enabled: true,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0.0, // 10% in prod, 0% in dev
    replaysOnErrorSampleRate: 1.0, // 100% for errors
  };

  // Use hardcoded config
  const config = hardcodedConfig;

  // Only initialize if enabled
  if (!config.enabled) {
    logger.debug("🔍 Sentry: Disabled");
    return;
  }

  logger.debug(`🔍 Sentry: Initializing for ${config.environment} environment`);

  Sentry.init({
    app,
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,

    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),

      // Session replay (for debugging)
      Sentry.replayIntegration({
        maskAllText: import.meta.env.PROD, // Mask text in production
        blockAllMedia: import.meta.env.PROD, // Block media in production
      }),
    ],

    // Performance monitoring sample rates
    tracesSampleRate: config.tracesSampleRate,

    // Session replay sample rates
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,

    // Error filtering and data scrubbing
    beforeSend(event, hint) {
      // Filter out development errors
      if (!import.meta.env.PROD) {
        // Log errors in development but still send to Sentry if enabled
        console.group("🔍 Sentry Error in Development:");
        logger.error(hint.originalException || hint.syntheticException);
        console.groupEnd();
      }

      // Filter out known non-critical errors
      const error = hint.originalException;
      if (error instanceof Error) {
        // Skip network errors from ad blockers, etc.
        if (
          error.message.includes("Network request failed") &&
          error.message.includes("chrome-extension://")
        ) {
          return null;
        }

        // Skip ResizeObserver errors (common browser quirk)
        if (error.message.includes("ResizeObserver loop limit exceeded")) {
          return null;
        }
      }

      // Scrub sensitive data
      if (event.request?.url) {
        // Remove sensitive query parameters
        event.request.url = event.request.url.replace(
          /([?&])(token|password|key|secret)=[^&]*/gi,
          "$1$2=***",
        );
      }

      // Scrub sensitive headers
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.Cookie;
      }

      return event;
    },

    // Additional configuration
    debug: !import.meta.env.PROD,
    autoSessionTracking: true,
    sendDefaultPii: false, // Don't send personally identifiable information

    // Set initial tags
    initialScope: {
      tags: {
        component: "scantixpro-vue",
        version: config.release,
        domain: window.location.hostname,
      },
    },
  });

  // Set up global error handlers
  app.config.errorHandler = (error, instance, info) => {
    logger.error("Vue Error:", error, info);

    Sentry.withScope((scope) => {
      scope.setTag("vue.error", true);
      scope.setContext("vue", {
        componentName: instance?.$options.name || "Unknown",
        lifecycleHook: info,
      });
      Sentry.captureException(error);
    });
  };

  // Add Sentry to global properties for easy access
  app.config.globalProperties.$sentry = Sentry;

  logger.debug("✅ Sentry: Successfully initialized");
}

// Export utilities for use in components
export { Sentry };

// Helper functions for common Sentry operations
export const SentryUtils = {
  // Capture a custom error with context
  captureError(error: Error, context?: Record<string, unknown>) {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureException(error);
    });
  },

  // Capture a custom message with level
  captureMessage(
    message: string,
    level: Sentry.SeverityLevel = "info",
    context?: Record<string, unknown>,
  ) {
    Sentry.withScope((scope) => {
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureMessage(message, level);
    });
  },

  // Set user context
  setUser(user: {
    id?: string;
    email?: string;
    username?: string;
    [key: string]: unknown;
  }) {
    Sentry.setUser(user);
  },

  // Clear user context
  clearUser() {
    Sentry.setUser(null);
  },

  // Add breadcrumb
  addBreadcrumb(
    message: string,
    category?: string,
    level?: Sentry.SeverityLevel,
    data?: Record<string, unknown>,
  ) {
    Sentry.addBreadcrumb({
      message,
      category: category || "custom",
      level: level || "info",
      data,
      timestamp: Date.now() / 1000,
    });
  },

  // Set tag
  setTag(key: string, value: string) {
    Sentry.setTag(key, value);
  },

  // Set context
  setContext(key: string, context: Record<string, unknown>) {
    Sentry.setContext(key, context);
  },
};
